import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Button } from "../../../components/ui/button";
import { Plus, Edit, Trash2, Loader2, Eye } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { toast } from "sonner";
import { blogsAPI, type Blog, type BlogCategory } from "../../../services/api";

const initialFormState = {
  title: "",
  sub_title: "",
  description: "",
  image: null as File | null,
  video: "",
  date: "",
  is_enabled: true,
  category: "",
};

function getCategoryLabel(categories: BlogCategory[], value: string) {
  if (!value) {
    return "—";
  }
  const cat = categories.find(c => String(c.id) === value);
  return cat?.name ?? value;
}

export default function Blogs() {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [viewBlogId, setViewBlogId] = useState<string | number | null>(null);
  const [viewBlog, setViewBlog] = useState<Blog | null>(null);
  const [loadingView, setLoadingView] = useState(false);
  const [form, setForm] = useState(initialFormState);

  const loadBlogs = async () => {
    setLoading(true);
    try {
      const data = await blogsAPI.list();
      setBlogs(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load blogs");
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await blogsAPI.categories();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load blog categories");
      setCategories([]);
    }
  };

  useEffect(() => {
    loadBlogs();
    loadCategories();
  }, []);

  const resetForm = () => {
    setForm(initialFormState);
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setEditingId(null);
    setIsDialogOpen(true);
  };

  const openEditModal = (blog: Blog) => {
    setForm({
      title: blog.title ?? "",
      sub_title: blog.sub_title ?? "",
      description: blog.description ?? "",
      image: null,
      video: blog.video ?? "",
      date: blog.date ?? "",
      is_enabled: blog.is_enabled ?? true,
      category:
        blog.category !== null && blog.category !== undefined
          ? String(blog.category)
          : "",
    });
    setEditingId(blog.id);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Please enter a title.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        sub_title: form.sub_title ?? undefined,
        description: form.description ?? undefined,
        category: form.category ?? undefined,
        date: form.date ?? undefined,
        is_enabled: form.is_enabled,
        image: form.image ?? undefined,
        video: form.video ?? undefined,
      };

      console.log(payload, "payload-----------");
      if (editingId !== null && editingId !== undefined) {
        await blogsAPI.partialUpdate(editingId, payload);
        toast.success("Blog updated successfully!");
      } else {
        await blogsAPI.create(payload);
        toast.success("Blog created successfully!");
      }
      await loadBlogs();
      setIsDialogOpen(false);
      resetForm();
    } catch {
      toast.error(
        editingId !== null && editingId !== undefined
          ? "Failed to update blog"
          : "Failed to create blog"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    setIsDialogOpen(open);
  };

  const handleDeleteClick = (id: string | number) => setDeleteId(id);

  const openViewModal = async (blog: Blog) => {
    setViewBlogId(blog.id);
    setViewBlog(null);
    setLoadingView(true);
    try {
      const res = await blogsAPI.get(blog.id);
      const data =
        res && typeof res === "object" && "data" in res
          ? (res as { data: Blog }).data
          : (res as Blog);
      setViewBlog(data ?? null);
    } catch {
      toast.error("Failed to load blog details");
      setViewBlogId(null);
    } finally {
      setLoadingView(false);
    }
  };

  const closeViewModal = () => {
    setViewBlogId(null);
    setViewBlog(null);
  };

  const handleDeleteConfirm = async () => {
    if (deleteId === null) {
      return;
    }
    try {
      await blogsAPI.delete(deleteId);
      toast.success("Blog deleted successfully!");
      await loadBlogs();
      setDeleteId(null);
    } catch {
      toast.error("Failed to delete blog");
    }
  };

  const isEditMode = editingId !== null && editingId !== undefined;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Blogs Management
          </h1>
          <p className="text-muted-foreground">
            Add and manage blog posts for super admin
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={openAddModal}>
              <Plus className="h-4 w-4" />
              Add Blog
            </Button>
          </DialogTrigger>
          <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>
                {isEditMode ? "Edit Blog" : "Add New Blog"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto px-2">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="My Blog Post"
                  value={form.title}
                  onChange={e =>
                    setForm(prev => ({ ...prev, title: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub_title">Sub Title</Label>
                <Input
                  id="sub_title"
                  placeholder="Subtitle here"
                  value={form.sub_title}
                  onChange={e =>
                    setForm(prev => ({ ...prev, sub_title: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Blog description content"
                  rows={4}
                  value={form.description}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={value =>
                    setForm(prev => ({ ...prev, category: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select blog category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories
                      .filter(c => c.is_active)
                      .map(cat => (
                        <SelectItem key={cat.id} value={String(cat.id)}>
                          {cat.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              {/* <div className="space-y-2">
                <Label>Image</Label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 rounded-lg border border-dashed bg-muted/30 p-4">
                    <label className="flex flex-1 cursor-pointer items-center gap-2 text-muted-foreground hover:text-foreground">
                      <Upload className="h-4 w-4" />
                      <span className="text-sm">
                        {form.image
                          ? form.image.name
                          : isEditMode &&
                              imagePreview &&
                              typeof imagePreview === "string"
                            ? "Current image (choose new to replace)"
                            : "Select files"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>
                  {imagePreview && (
                    <div className="relative h-32 w-full overflow-hidden rounded-lg border bg-muted">
                      {typeof imagePreview === "string" &&
                      (imagePreview.startsWith("http") ||
                        imagePreview.startsWith("blob")) ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                  )}
                </div>
              </div> */}
              <div className="space-y-2">
                <Label htmlFor="video">YouTube Video Link</Label>
                <Input
                  id="video"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={form.video}
                  onChange={e =>
                    setForm(prev => ({ ...prev, video: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={form.date}
                  onChange={e =>
                    setForm(prev => ({ ...prev, date: e.target.value }))
                  }
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_enabled"
                  checked={form.is_enabled}
                  onCheckedChange={checked =>
                    setForm(prev => ({
                      ...prev,
                      is_enabled: checked === true,
                    }))
                  }
                />
                <Label
                  htmlFor="is_enabled"
                  className="cursor-pointer text-sm font-normal"
                >
                  Enable blog (visible to users)
                </Label>
              </div>
            </div>
            <div className="mt-4 flex shrink-0 justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditMode ? "Update Blog" : "Save Blog"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Blogs ({blogs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Sub Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blogs.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      No blogs yet. Click &quot;Add Blog&quot; to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  blogs.map(blog => (
                    <TableRow key={String(blog.id)}>
                      <TableCell className="font-medium">
                        {blog.title}
                      </TableCell>
                      <TableCell>{blog.sub_title ?? "—"}</TableCell>
                      <TableCell>
                        {blog.category_name ??
                          getCategoryLabel(
                            categories,
                            blog.category !== null &&
                              blog.category !== undefined
                              ? String(blog.category)
                              : ""
                          )}
                      </TableCell>
                      <TableCell>{blog.date ?? "—"}</TableCell>
                      <TableCell>{blog.is_enabled ? "Yes" : "No"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openViewModal(blog)}
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditModal(blog)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteClick(blog.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={deleteId !== null && deleteId !== undefined}
        onOpenChange={open => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete blog</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              blog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="text-destructive-foreground bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Blog Dialog */}
      <Dialog
        open={viewBlogId !== null}
        onOpenChange={open => !open && closeViewModal()}
      >
        <DialogContent className="flex max-h-[90vh] max-w-xl flex-col overflow-hidden p-0">
          {loadingView ? (
            <div className="flex min-h-[200px] items-center justify-center py-12">
              <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
            </div>
          ) : viewBlog ? (
            <>
              <DialogHeader className="shrink-0 border-b px-6 py-4">
                <DialogTitle className="text-xl">Blog Details</DialogTitle>
              </DialogHeader>
              <div className="flex flex-1 flex-col overflow-y-auto">
                {viewBlog.video ? (
                  <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-black">
                    {(() => {
                      const videoUrl = viewBlog.video;
                      let videoId = "";
                      if (videoUrl.includes("youtu.be/")) {
                        videoId = videoUrl.split("youtu.be/")[1]?.split("?")[0];
                      } else if (videoUrl.includes("v=")) {
                        videoId = videoUrl.split("v=")[1]?.split("&")[0];
                      } else if (videoUrl.includes("embed/")) {
                        videoId = videoUrl.split("embed/")[1]?.split("?")[0];
                      }

                      if (videoId) {
                        return (
                          <iframe
                            className="h-full w-full"
                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        );
                      }
                      return (
                        <div className="flex h-full items-center justify-center text-sm text-white">
                          Invalid Video URL
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center border-b bg-muted/50 text-sm text-muted-foreground">
                    Video link not provided
                  </div>
                )}
                {/* {(viewBlog.banner ?? viewBlog.image) && (
                  <div className="relative h-48 w-full shrink-0 overflow-hidden bg-muted">
                    <img
                      src={
                        typeof viewBlog.banner === "string"
                          ? viewBlog.banner
                          : ((viewBlog.image as string) ?? "")
                      }
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </div>
                )} */}
                <div className="flex flex-col gap-4 p-6">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">
                      {viewBlog.title}
                    </h2>
                    {viewBlog.sub_title && (
                      <p className="mt-1 text-muted-foreground">
                        {viewBlog.sub_title}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">
                      Category:
                    </span>
                    <span className="rounded-md bg-muted px-2 py-0.5 text-sm">
                      {viewBlog.category_name ??
                        getCategoryLabel(
                          categories,
                          viewBlog.category !== null
                            ? String(viewBlog.category)
                            : ""
                        )}
                    </span>
                    {viewBlog.date && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-sm text-muted-foreground">
                          {viewBlog.date}
                        </span>
                      </>
                    )}
                    <span
                      className={`ml-auto rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        viewBlog.is_enabled
                          ? "bg-green-100 text-green-800"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {viewBlog.is_enabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  {viewBlog.description && (
                    <div className="rounded-lg border bg-muted/30 p-4">
                      <p className="whitespace-pre-wrap text-sm text-foreground">
                        {viewBlog.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 justify-end gap-2 border-t px-6 py-4">
                <Button variant="outline" onClick={closeViewModal}>
                  Close
                </Button>
                <Button
                  onClick={() => {
                    closeViewModal();
                    openEditModal(viewBlog);
                  }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Blog
                </Button>
              </div>
            </>
          ) : (
            <div className="flex min-h-[120px] items-center justify-center py-8 text-muted-foreground">
              No blog data to display.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
